import React, { useCallback, useMemo, useRef, useState } from "react";

import {
  formatExpectedReturnPctWithPa,
  formatProjectCodeDisplay,
  getMarketStageToneClass,
  getProjectLifecycleLabel,
} from "../../utils/projectViews";
import { issuePricePerShare, shareValueBuyPlusPnl } from "../../utils/investmentPricing";
import {
  PRICE_RANGE_OPTIONS,
  filterPriceHistoryByRange,
  priceHistoryRangeCaption,
  sortPriceHistoryChronological,
} from "../../utils/priceHistoryUtils";

const CHART_FULL = { w: 560, h: 220, pad: { top: 28, right: 28, bottom: 28, left: 40 } };
const CHART_COMPACT = { h: 210, pad: { top: 28, right: 14, bottom: 26, left: 36 } };
const CHART_DASHBOARD = { h: 132, pad: { top: 16, right: 10, bottom: 24, left: 44 } };

const CHART_LAYOUT = {
  full: { pad: CHART_FULL.pad, h: CHART_FULL.h, step: 36, minW: 360, maxW: 560 },
  compact: { pad: CHART_COMPACT.pad, h: CHART_COMPACT.h, step: 32, minW: 300, maxW: 520 },
  /** Fixed viewBox width so the plot fills the card (no horizontal letterboxing). */
  dashboard: { pad: CHART_DASHBOARD.pad, h: CHART_DASHBOARD.h, fixedW: 280 },
};

/** Chart SVG dimensions — dashboard uses a fixed width; others scale mildly with point count. */
function resolveChartDimensions(pointCount, layout = "full") {
  const cfg = CHART_LAYOUT[layout] || CHART_LAYOUT.full;
  if (cfg.fixedW) {
    return { w: cfg.fixedW, h: cfg.h, pad: cfg.pad };
  }
  const n = Math.max(pointCount, 1);
  const w = Math.min(
    cfg.maxW,
    Math.max(cfg.minW, cfg.pad.left + cfg.pad.right + Math.max(n - 1, 0) * cfg.step + (n <= 1 ? 40 : 12))
  );
  return { w, h: cfg.h, pad: cfg.pad };
}

/** Match Live NAV card formatting (2 dp). */
function formatNavPerShare(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toFixed(2);
}

function formatAxisDate(iso, { withTime = false } = {}) {
  if (!iso) return "";
  const d = new Date(iso);
  if (withTime) {
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Y-axis labels adapt to NAV range (avoid duplicate integers when span is small). */
function formatAxisPrice(n, priceSpan) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const span = Number(priceSpan);
  if (span > 0 && span < 0.5) return v.toFixed(3);
  if (span > 0 && span < 5) return v.toFixed(2);
  if (span > 0 && span < 80) return v.toFixed(1);
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatPct(n) {
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}

function buildChartModel(points, { w: CHART_W, h: CHART_H, pad: PAD }, layout = "full") {
  if (!points.length) return null;
  const prices = points.map((p) => Number(p.price) || 0);
  const rawMin = Math.min(...prices);
  const rawMax = Math.max(...prices);
  const priceSpan = rawMax - rawMin || Math.max(rawMax * 0.002, 0.01);
  const padSpan = Math.max(priceSpan, rawMax * 0.008, 0.02);
  const yMin = rawMin - padSpan * 0.35;
  const yMax = rawMax + padSpan * 0.35;
  const innerW = CHART_W - PAD.left - PAD.right;
  const innerH = CHART_H - PAD.top - PAD.bottom;
  const bottom = PAD.top + innerH;

  const coords = points.map((p, i) => {
    const price = Number(p.price) || 0;
    const x = PAD.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
    const y = PAD.top + innerH - ((price - yMin) / (yMax - yMin)) * innerH;
    return { x, y, price, recorded_at: p.recorded_at };
  });

  const linePath = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x.toFixed(2)} ${c.y.toFixed(2)}`).join(" ");
  const areaPath = `${linePath} L ${coords[coords.length - 1].x.toFixed(2)} ${bottom} L ${coords[0].x.toFixed(2)} ${bottom} Z`;

  const yRange = yMax - yMin;
  const tickFracs =
    layout === "dashboard" || priceSpan < 5 ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1];
  const yTicks = tickFracs.map((t) => ({
    y: PAD.top + innerH - t * innerH,
    label: formatAxisPrice(yMin + t * yRange, priceSpan),
  }));

  const xLabelIndices =
    points.length <= 4 ? points.map((_, i) => i) : [0, Math.floor((points.length - 1) / 2), points.length - 1];

  const dayKey = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toDateString();
  };
  const sameCalendarDay =
    points.length > 1 && dayKey(points[0].recorded_at) === dayKey(points[points.length - 1].recorded_at);

  const xLabels = xLabelIndices.map((i) => ({
    x: coords[i].x,
    label: formatAxisDate(points[i].recorded_at, { withTime: sameCalendarDay }),
  }));

  const plotTop = PAD.top;
  const plotBottom = bottom;
  const plotLeft = PAD.left;
  const plotRight = CHART_W - PAD.right;

  return { coords, linePath, areaPath, yTicks, xLabels, plotTop, plotBottom, plotLeft, plotRight };
}

function formatHoverDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Snap hover to the nearest snapshot along the x-axis. */
function pickHoverPoint(coords, svgX) {
  if (!coords?.length) return null;
  let bestIdx = 0;
  let bestDist = Infinity;
  coords.forEach((c, i) => {
    const d = Math.abs(c.x - svgX);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  });
  const c = coords[bestIdx];
  return { index: bestIdx, x: c.x, y: c.y, price: c.price, recorded_at: c.recorded_at };
}

function svgPointFromClient(svg, clientX, clientY) {
  if (!svg?.createSVGPoint) return null;
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  return pt.matrixTransform(ctm.inverse());
}

function hoverLabelLayout(point, chartSize) {
  const tx = Math.min(Math.max(point.x, chartSize.pad.left + 36), chartSize.w - chartSize.pad.right - 36);
  const roomAbove = point.y - chartSize.pad.top;
  const placeAbove = roomAbove >= 28;
  const priceY = placeAbove ? point.y - 18 : point.y + 14;
  const dateY = placeAbove ? point.y - 6 : point.y + 26;
  return { tx, priceY, dateY, placeAbove };
}

function InteractivePriceChart({
  chart,
  chartSize,
  gradientId,
  liveNav,
  ariaLabel,
  chartLayout,
}) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [focused, setFocused] = useState(false);

  const updateHover = useCallback(
    (clientX, clientY) => {
      const svg = svgRef.current;
      if (!svg || !chart) return;
      const pt = svgPointFromClient(svg, clientX, clientY);
      if (!pt) return;
      if (pt.x < chart.plotLeft || pt.x > chart.plotRight || pt.y < chart.plotTop || pt.y > chart.plotBottom) {
        setHover(null);
        return;
      }
      setHover(pickHoverPoint(chart.coords, pt.x));
    },
    [chart]
  );

  const onPointerMove = useCallback(
    (e) => {
      updateHover(e.clientX, e.clientY);
    },
    [updateHover]
  );

  const onPointerLeave = useCallback(() => setHover(null), []);
  const onPointerDown = useCallback(
    (e) => {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      updateHover(e.clientX, e.clientY);
    },
    [updateHover]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (!chart?.coords?.length) return;
      const n = chart.coords.length;
      const base = hover?.index ?? n - 1;
      let next = base;
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = Math.max(0, base - 1);
      else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = Math.min(n - 1, base + 1);
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = n - 1;
      else return;
      e.preventDefault();
      const c = chart.coords[next];
      setHover({ index: next, x: c.x, y: c.y, price: c.price, recorded_at: c.recorded_at });
      setFocused(true);
    },
    [chart, hover?.index]
  );

  const active = hover ?? (chart.coords.length ? {
    index: chart.coords.length - 1,
    x: chart.coords[chart.coords.length - 1].x,
    y: chart.coords[chart.coords.length - 1].y,
    price: liveNav,
    recorded_at: chart.coords[chart.coords.length - 1].recorded_at,
  } : null);

  const showLiveEndpoint = !hover;
  const labelPoint = active;
  const { tx, priceY, dateY } = labelPoint ? hoverLabelLayout(labelPoint, chartSize) : { tx: 0, priceY: 0, dateY: 0 };

  return (
    <div className="investor-price-update__chart-interactive">
      <svg
        ref={svgRef}
        className="investor-price-update__chart-svg investor-price-update__chart-svg--interactive"
        viewBox={`0 0 ${chartSize.w} ${chartSize.h}`}
        width="100%"
        preserveAspectRatio={chartLayout === "full" ? "xMidYMid meet" : "xMinYMid meet"}
        role="application"
        aria-label={ariaLabel}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setHover(null);
        }}
        onPointerMove={onPointerMove}
        onPointerDown={onPointerDown}
        onPointerUp={(e) => e.currentTarget.releasePointerCapture?.(e.pointerId)}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerLeave}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e7bb8" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#1e7bb8" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {chart.yTicks.map((tick, tickIdx) => (
          <g key={`ytick-${tickIdx}`}>
            <line
              x1={chart.plotLeft}
              y1={tick.y}
              x2={chart.plotRight}
              y2={tick.y}
              className="investor-price-update__grid-line"
            />
            <text x={chart.plotLeft - 8} y={tick.y + 4} className="investor-price-update__axis-y" textAnchor="end">
              {tick.label}
            </text>
          </g>
        ))}
        <path d={chart.areaPath} fill={`url(#${gradientId})`} />
        <path d={chart.linePath} className="investor-price-update__line" fill="none" pointerEvents="none" />
        <rect
          x={chart.plotLeft}
          y={chart.plotTop}
          width={chart.plotRight - chart.plotLeft}
          height={chart.plotBottom - chart.plotTop}
          fill="transparent"
          className="investor-price-update__plot-hit"
        />
        {hover ? (
          <line
            x1={hover.x}
            y1={chart.plotTop}
            x2={hover.x}
            y2={chart.plotBottom}
            className="investor-price-update__crosshair"
            pointerEvents="none"
          />
        ) : null}
        {chart.coords.map((c, i) => {
          const isActive = active?.index === i;
          if (!hover && !isActive) return null;
          return (
            <circle
              key={`${c.recorded_at}-${i}`}
              cx={c.x}
              cy={c.y}
              r={isActive ? 5 : 3}
              className={`investor-price-update__dot${isActive ? " investor-price-update__dot--active" : " investor-price-update__dot--ghost"}`}
              pointerEvents="none"
            />
          );
        })}
        {labelPoint && chartLayout !== "dashboard" ? (
          <g className="investor-price-update__hover-label" pointerEvents="none">
            <text x={tx} y={priceY} textAnchor="middle" className="investor-price-update__hover-price">
              {formatNavPerShare(showLiveEndpoint ? liveNav : labelPoint.price)} BDT
            </text>
            <text x={tx} y={dateY} textAnchor="middle" className="investor-price-update__hover-date">
              {formatHoverDate(labelPoint.recorded_at)}
            </text>
          </g>
        ) : null}
        {chart.xLabels.map((xl) => (
          <text
            key={`${xl.label}-${xl.x}`}
            x={xl.x}
            y={chartSize.h - 8}
            className="investor-price-update__axis-x"
            textAnchor="middle"
            pointerEvents="none"
          >
            {xl.label}
          </text>
        ))}
      </svg>
      <p
        className={`investor-price-update__hover-readout${hover || focused ? " investor-price-update__hover-readout--live" : ""}${chartLayout === "dashboard" ? " investor-price-update__hover-readout--dashboard" : ""}`}
        aria-live="polite"
      >
        {active ? (
          <>
            <strong>{formatNavPerShare(showLiveEndpoint ? liveNav : active.price)} BDT/sh</strong>
            <span className="investor-price-update__hover-readout-sep">·</span>
            <span>{formatHoverDate(active.recorded_at)}</span>
          </>
        ) : chartLayout === "dashboard" ? (
          <span className="muted">Hover chart for snapshot price</span>
        ) : (
          <span className="muted">Move cursor over the chart to see price at each snapshot</span>
        )}
      </p>
    </div>
  );
}

/**
 * @param {"full"|"compact"|"dashboard"} variant — `compact` funded tab; `dashboard` smaller in-portfolio charts.
 */
export default function ProjectPriceUpdatePanel({
  project,
  priceSeries = [],
  onRefresh,
  refreshing = false,
  actions = null,
  variant = "full",
  holdingShares = null,
  trailingAside = null,
  headerExtra = null,
  /** Project code/name row (hide when already shown by a parent toggle). */
  showProjectTitle = true,
}) {
  const [range, setRange] = useState("1M");
  const [refreshError, setRefreshError] = useState(false);
  const isDashboard = variant === "dashboard";
  const isTightLayout = variant === "compact" || isDashboard;
  const chartLayout = isDashboard ? "dashboard" : variant === "compact" ? "compact" : "full";

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshError(false);
    try {
      await onRefresh();
    } catch {
      setRefreshError(true);
    }
  };

  const listing = issuePricePerShare(project);
  const currentNav = shareValueBuyPlusPnl(project);

  const sortedSeries = useMemo(() => sortPriceHistoryChronological(priceSeries), [priceSeries]);
  const filtered = useMemo(() => filterPriceHistoryByRange(sortedSeries, range), [sortedSeries, range]);
  const rangeCaption = useMemo(() => priceHistoryRangeCaption(sortedSeries, range), [sortedSeries, range]);
  const activeChartSize = useMemo(
    () => resolveChartDimensions(filtered.length, chartLayout),
    [filtered.length, chartLayout]
  );
  const hasAnySnapshots = sortedSeries.length > 0;

  const lastPoint = filtered[filtered.length - 1];
  const prevPoint = filtered.length > 1 ? filtered[filtered.length - 2] : null;
  const lastSnapshotPrice = lastPoint ? Number(lastPoint.price) : null;
  /** Authoritative live NAV (API); chart tip uses this so it matches the metrics card. */
  const liveNav = Number.isFinite(currentNav) && currentNav > 0 ? currentNav : lastSnapshotPrice ?? 0;

  /** Plot live NAV at the line end when it differs from the last stored snapshot. */
  const chartSeries = useMemo(() => {
    if (!filtered.length) return filtered;
    if (!Number.isFinite(liveNav) || liveNav <= 0) return filtered;
    const last = filtered[filtered.length - 1];
    if (Math.abs(Number(last.price) - liveNav) < 0.0005) return filtered;
    return [...filtered.slice(0, -1), { ...last, price: liveNav }];
  }, [filtered, liveNav]);

  const chart = useMemo(
    () => buildChartModel(chartSeries, activeChartSize, chartLayout),
    [chartSeries, activeChartSize, chartLayout]
  );

  const vsListingNavPct = listing > 0 ? ((currentNav - listing) / listing) * 100 : 0;
  const vsListingSnapshotPct = listing > 0 ? ((liveNav - listing) / listing) * 100 : 0;
  const dailyDelta = prevPoint && lastSnapshotPrice != null ? lastSnapshotPrice - Number(prevPoint.price) : 0;
  const dailyPct = prevPoint && Number(prevPoint.price) > 0 ? (dailyDelta / Number(prevPoint.price)) * 100 : 0;

  const estReturn = formatExpectedReturnPctWithPa(project);
  const estReturnMatch = estReturn && String(estReturn).match(/([\d.]+)/);
  const estReturnNum = estReturnMatch ? Number(estReturnMatch[1]) : null;
  const gradientId = `price-area-${String(project?.id || "x").replace(/[^a-zA-Z0-9]/g, "")}`;

  const code = formatProjectCodeDisplay(project?.project_code) || "—";
  const name = project?.name || "—";
  const stageLabel = project?.status ? getProjectLifecycleLabel(project.status) : "—";

  const lastUpdated = lastPoint?.recorded_at
    ? new Date(lastPoint.recorded_at).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

  const chartBlock = (
    <div className="investor-price-update__chart-panel">
      <div className="investor-price-update__chart-head">
        <h5 className="investor-price-update__chart-title">Price Update (BDT/sh)</h5>
        <div className="investor-price-update__ranges" role="tablist" aria-label="Chart timeframe">
          {PRICE_RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              role="tab"
              aria-selected={range === opt.key}
              className={`investor-price-update__range-btn${range === opt.key ? " investor-price-update__range-btn--active" : ""}`}
              onClick={() => setRange(opt.key)}
            >
              {opt.key}
            </button>
          ))}
        </div>
      </div>

      {!hasAnySnapshots ? (
        <p className="muted investor-price-update__empty">No price snapshots yet.</p>
      ) : !chart ? (
        <p className="muted investor-price-update__empty">
          No snapshots in this period. Try <strong>All</strong> or a longer range.
        </p>
      ) : (
        <div className="investor-price-update__chart-wrap" key={range}>
          {rangeCaption ? <p className="investor-price-update__range-caption muted">{rangeCaption}</p> : null}
          <InteractivePriceChart
            chart={chart}
            chartSize={activeChartSize}
            gradientId={gradientId}
            liveNav={liveNav}
            chartLayout={chartLayout}
            ariaLabel={`Share price from ${formatAxisDate(filtered[0]?.recorded_at)} to ${formatAxisDate(lastPoint?.recorded_at)}. Use pointer or arrow keys to explore snapshots.`}
          />
        </div>
      )}

      <footer className="investor-price-update__footer">
        <span className="investor-price-update__updated">
          {lastUpdated ? (
            <>
              <span className="investor-price-update__clock" aria-hidden>
                🕐
              </span>
              Last updated: {lastUpdated}
            </>
          ) : (
            "No updates recorded"
          )}
        </span>
        {onRefresh ? (
          <button
            type="button"
            className="investor-price-update__refresh"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-busy={refreshing}
          >
            {refreshing ? "Refreshing…" : "↻ Refresh"}
          </button>
        ) : null}
        {refreshError ? (
          <span className="investor-price-update__refresh-error" role="status">
            Could not refresh. Try again.
          </span>
        ) : null}
      </footer>
    </div>
  );

  const metricsAside = isTightLayout ? (
    <aside className="investor-price-update__metrics investor-price-update__metrics--compact" aria-label="Key metrics">
      <div className="investor-price-update__metric investor-price-update__metric--blue">
        <span className="investor-price-update__metric-label">Live NAV / share</span>
        <div className="investor-price-update__metric-row">
          <strong>{formatNavPerShare(liveNav)} BDT/sh</strong>
          {listing > 0 ? (
            <span
              className={`investor-price-update__delta${vsListingNavPct >= 0 ? " investor-price-update__delta--up" : " investor-price-update__delta--down"}`}
            >
              {vsListingNavPct >= 0 ? "↑" : "↓"} {formatPct(Math.abs(vsListingNavPct))}
            </span>
          ) : null}
        </div>
        <span className="investor-price-update__metric-sub muted">
          Listing {listing.toFixed(2)} BDT/sh
          {hasAnySnapshots &&
          lastSnapshotPrice != null &&
          Math.abs(lastSnapshotPrice - currentNav) > 0.005
            ? ` · Last snapshot ${formatNavPerShare(lastSnapshotPrice)}`
            : null}
        </span>
      </div>

      {estReturnNum != null ? (
        <div className="investor-price-update__metric investor-price-update__metric--purple">
          <span className="investor-price-update__metric-label">Est. return (p.a.)</span>
          <div className="investor-price-update__metric-row">
            <strong>{estReturnNum.toFixed(2)}%</strong>
            <span className="investor-price-update__target-badge">◎ Target</span>
          </div>
        </div>
      ) : null}

      {prevPoint ? (
        <div
          className={`investor-price-update__metric ${
            dailyDelta < -0.0001 ? "investor-price-update__metric--loss" : "investor-price-update__metric--gain"
          }`}
        >
          <span className="investor-price-update__metric-label">Recent changes</span>
          <div className="investor-price-update__metric-row">
            <strong>
              {dailyDelta >= 0 ? "+" : ""}
              {dailyDelta.toFixed(2)} BDT
            </strong>
            <span
              className={`investor-price-update__delta${dailyDelta >= 0 ? " investor-price-update__delta--up" : " investor-price-update__delta--down"}`}
            >
              {dailyDelta >= 0 ? "↑" : "↓"} {formatPct(Math.abs(dailyPct))}
            </span>
          </div>
          <span className="investor-price-update__metric-sub muted">vs. previous snapshot</span>
        </div>
      ) : null}
    </aside>
  ) : (
    <aside className="investor-price-update__metrics" aria-label="Price metrics">
      <div className="investor-price-update__metric investor-price-update__metric--blue">
        <span className="investor-price-update__metric-label">Current Share Price</span>
        <div className="investor-price-update__metric-row">
          <strong>{formatNavPerShare(liveNav)} BDT/sh</strong>
          <span
            className={`investor-price-update__delta${vsListingSnapshotPct >= 0 ? " investor-price-update__delta--up" : " investor-price-update__delta--down"}`}
          >
            {vsListingSnapshotPct >= 0 ? "↑" : "↓"} {formatPct(Math.abs(vsListingSnapshotPct))}
          </span>
        </div>
        <span className="investor-price-update__metric-sub muted">vs. Listing (live NAV)</span>
      </div>

      <div className="investor-price-update__metric investor-price-update__metric--green">
        <span className="investor-price-update__metric-label">Unrealised Value</span>
        <strong>{formatNavPerShare(liveNav)} BDT/sh</strong>
        <span className="investor-price-update__metric-sub muted">{formatPct(vsListingNavPct)} vs. Listing</span>
      </div>

      <div className="investor-price-update__metric investor-price-update__metric--purple">
        <span className="investor-price-update__metric-label">Est. Return (p.a.)</span>
        <div className="investor-price-update__metric-row">
          <strong>{estReturnNum != null ? `${estReturnNum.toFixed(2)}%` : "—"}</strong>
          <span className="investor-price-update__target-badge">◎ Target</span>
        </div>
      </div>

      <div
        className={`investor-price-update__metric ${
          dailyDelta < -0.0001 ? "investor-price-update__metric--loss" : "investor-price-update__metric--gain"
        }`}
      >
        <span className="investor-price-update__metric-label">Recent changes</span>
        <div className="investor-price-update__metric-row">
          <strong>
            {dailyDelta >= 0 ? "+" : ""}
            {dailyDelta.toFixed(2)} BDT
          </strong>
          {prevPoint ? (
            <span
              className={`investor-price-update__delta${dailyDelta >= 0 ? " investor-price-update__delta--up" : " investor-price-update__delta--down"}`}
            >
              {dailyDelta >= 0 ? "↑" : "↓"} {formatPct(Math.abs(dailyPct))}
            </span>
          ) : null}
        </div>
        <span className="investor-price-update__metric-sub muted">vs. Previous Day</span>
      </div>
    </aside>
  );

  const holdingLine =
    holdingShares != null && Number(holdingShares) > 0 ? (
      <p className="investor-price-update__holding-line muted">
        Your holding: <strong>{holdingShares}</strong> shares
      </p>
    ) : null;
  const showHeaderTop = showProjectTitle;
  const showHeaderMeta = !isTightLayout;
  const showHeaderExtras = isTightLayout && (headerExtra || holdingLine);
  const showHeader = showHeaderTop || showHeaderMeta || showHeaderExtras;

  return (
    <article
      className={`investor-price-update${isTightLayout ? " investor-price-update--compact" : ""}${isDashboard ? " investor-price-update--dashboard" : ""}${!showProjectTitle ? " investor-price-update--no-title" : ""}`}
    >
      {showHeader ? (
        <header className={`investor-price-update__header${isTightLayout ? " investor-price-update__header--compact" : ""}`}>
          {showHeaderTop ? (
            <div className="investor-price-update__header-top">
              <h4 className="investor-price-update__title">
                <span className="investor-price-update__icon" aria-hidden>
                  🏥
                </span>
                {code} – {name}
              </h4>
              <span className={`invx-market-stage ${getMarketStageToneClass(project?.status)}`}>{stageLabel}</span>
            </div>
          ) : null}
          {showHeaderMeta ? (
            <div className="investor-price-update__header-meta">
              <span>Est. Ret. {estReturn ?? "—"}</span>
              <span>Listing {listing.toFixed(2)} BDT/sh</span>
              <span className="investor-price-update__nav-pill">Unrealised Value {formatNavPerShare(liveNav)} BDT/sh</span>
            </div>
          ) : null}
          {showHeaderExtras ? (
            <>
              {headerExtra}
              {holdingLine}
            </>
          ) : null}
        </header>
      ) : null}

      <div
        className={`investor-price-update__body${variant === "compact" && trailingAside ? " investor-price-update__body--with-visual" : ""}`}
      >
        {chartBlock}
        {metricsAside}
        {variant === "compact" && trailingAside ? trailingAside : null}
      </div>
      {actions ? <div className="investor-price-update__actions">{actions}</div> : null}
    </article>
  );
}
